'use client';

import NoGutterRow from '@/components/layout/NoGutterRow';
import { formatCurrency } from '@/features/orders/orderPresentation';
import NextImage from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Dropdown, Image } from 'react-bootstrap';
import { readCartMessage } from './cartMessages';
import { calculateItemDiscount } from './cartUtils';

function getStockTone(stockCount) {
  if (stockCount <= 0) {
    return 'out';
  }

  if (stockCount <= 5) {
    return 'low';
  }

  return 'in';
}

export default function CartLineItem({
  item,
  messages,
  onQtyChange,
  onRemove,
}) {
  const qtyToggleRef = useRef(null);
  const [openQty, setOpenQty] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(null);
  const [toggleWidth, setToggleWidth] = useState(null);
  const { discountedPrice, freeQty, hasDiscount } = calculateItemDiscount(
    item,
    item.appliedBenefits || [],
  );
  const currency = item.currency || 'USD';
  const lineId = item.cartLineId || item.productNumber;
  const isLowStock =
    !item.isGift && item.countInStock > 0 && item.countInStock <= 5;
  const maxQty = Math.max(1, item.countInStock || item.qty || 1);
  const clampedQty = Math.min(item.qty, maxQty);
  const stockTone = getStockTone(Number(item.countInStock || 0));
  const stockLabel =
    stockTone === 'out'
      ? readCartMessage(messages, 'cartScreen', 'outOfStock', 'Out of stock')
      : stockTone === 'low'
        ? readCartMessage(
            messages,
            'cartScreen',
            'lowStock',
            'Only {{count}} left!',
          ).replace('{{count}}', String(item.countInStock || 0))
        : readCartMessage(messages, 'cartScreen', 'inStock', 'In stock');

  useEffect(() => {
    if (qtyToggleRef.current) {
      setToggleWidth(qtyToggleRef.current.offsetWidth);
    }
  }, [clampedQty, item.productNumber, openQty]);

  return (
    <article
      className={`cart-item checkout-summary checkout-summary--section ${isLowStock ? 'low-stock' : ''} ${openQty ? 'open-qty' : ''}`.trim()}
      aria-label={readCartMessage(
        messages,
        'cartScreen',
        'aria.cartItem',
        'Cart item: {{name}}',
      ).replace('{{name}}', item.name || '')}
    >
      <NoGutterRow className="cart-item__top-row mb-3">
        <Col xs={2} sm={3} className="cart-item__media-col mb-0">
          <div className="cart-img-wrapper">
            {item.coverImage ? (
              <span className="cart-img-frame">
                <NextImage
                  src={item.coverImage}
                  alt={item.name}
                  className="cart-img"
                  width={160}
                  height={160}
                  sizes="(max-width: 575.98px) 84px, (max-width: 991.98px) 112px, 128px"
                  loading="lazy"
                  draggable={false}
                />
              </span>
            ) : null}
          </div>
        </Col>
        <Col
          xs={10}
          sm={9}
          className="cart-item__content-col name-price-control d-flex flex-column gap-4"
        >
          <NoGutterRow className="cart-item__header-row gy-3">
            <Col xs={12} sm={8} className="cart-item__title-col">
              {item.productNumber ? (
                <Link
                  href={`/product/${item.productNumber}`}
                  className="cart-item__name"
                >
                  {item.name}
                  {freeQty > 0 ? (
                    <span className="ms-2 free-qty-badge text-success">
                      🎁 +{freeQty}{' '}
                      {readCartMessage(messages, 'cartScreen', 'free', 'FREE')}
                    </span>
                  ) : null}
                </Link>
              ) : (
                <span className="cart-item__name">
                  {item.name}
                  {freeQty > 0 ? (
                    <span className="ms-2 free-qty-badge text-success">
                      🎁 +{freeQty}{' '}
                      {readCartMessage(messages, 'cartScreen', 'free', 'FREE')}
                    </span>
                  ) : null}
                </span>
              )}
              <div className="product-card__meta mt-1">
                {item.productNumber}
              </div>
            </Col>
            <Col xs={12} sm={4} className="cart-item__price-col pe-0">
              <div className="cart-item__price-stack">
                {item.isGift ? (
                  <>
                    <span className="cart-item__price">
                      {readCartMessage(
                        messages,
                        'cartScreen',
                        'included',
                        'Included',
                      )}
                    </span>
                    {item.realPrice > 0 ? (
                      <del className="cart-item__price cart-item__price--original small">
                        {formatCurrency(item.realPrice, currency)}
                      </del>
                    ) : null}
                  </>
                ) : hasDiscount ? (
                  <>
                    <span className="cart-item__price cart-item__price--discounted">
                      {formatCurrency(discountedPrice, currency)}
                    </span>
                    <del className="cart-item__price cart-item__price--original small">
                      {formatCurrency(item.price, currency)}
                    </del>
                  </>
                ) : (
                  <span className="cart-item__price">
                    {formatCurrency(item.price, currency)}
                  </span>
                )}
              </div>
            </Col>
          </NoGutterRow>
        </Col>
      </NoGutterRow>

      <NoGutterRow className="cart-item__meta-row align-items-center mb-2">
        <Col className="cart-item__status-col text-sm-center">
          {!item.isGift ? (
            <span
              className={`cart-item__stock-badge ms-4 ms-sm-0 cart-item__stock-badge--${stockTone}`.trim()}
            >
              {stockLabel}
            </span>
          ) : null}
        </Col>

        {item.isGift ? (
          <Col xs="auto">
            <span className="cart-item__gift-badge small">
              {readCartMessage(messages, 'cartScreen', 'gift', 'Gift')}
            </span>
          </Col>
        ) : (
          <>
            <Col xs="auto" className="text-center">
              <span className="cart-item__qty-label me-2">
                {readCartMessage(messages, 'cartScreen', 'qty', 'Qty')}
              </span>
            </Col>
            <Col xs="auto">
              <label>
                <span className="visually-hidden">
                  {readCartMessage(
                    messages,
                    'cartScreen',
                    'aria.qtySelector',
                    'Select quantity for {{name}}',
                  ).replace('{{name}}', item.name || '')}
                </span>
                <Dropdown
                  show={openQty}
                  onToggle={(isOpen) => {
                    setOpenQty(isOpen);
                    if (isOpen) {
                      setFocusedIdx(clampedQty - 1);
                    }
                  }}
                  className="cart-item__qty-dropdown"
                >
                  <Dropdown.Toggle
                    id={`cart-item-qty-toggle-${lineId}`}
                    variant="outline-primary"
                    className="cart-item__qty-toggle"
                    aria-label={readCartMessage(
                      messages,
                      'cartScreen',
                      'aria.qtySelector',
                      'Select quantity for {{name}}',
                    ).replace('{{name}}', item.name || '')}
                    ref={qtyToggleRef}
                    onKeyDown={(event) => {
                      if (
                        event.key === 'ArrowDown' ||
                        event.key === 'ArrowUp'
                      ) {
                        event.preventDefault();
                        const direction = event.key === 'ArrowDown' ? 1 : -1;
                        const next = Math.min(
                          Math.max(
                            (focusedIdx ?? clampedQty - 1) + direction,
                            0,
                          ),
                          maxQty - 1,
                        );
                        setOpenQty(true);
                        setFocusedIdx(next);
                        return;
                      }

                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();

                        if (!openQty) {
                          setOpenQty(true);
                          setFocusedIdx(clampedQty - 1);
                          return;
                        }

                        onQtyChange(lineId, (focusedIdx ?? clampedQty - 1) + 1);
                        setOpenQty(false);
                        return;
                      }

                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setOpenQty(false);
                      }
                    }}
                  >
                    {clampedQty}
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    className="cart-item__qty-menu"
                    style={{
                      width: toggleWidth ? `${toggleWidth}px` : undefined,
                    }}
                    role="listbox"
                    aria-label={readCartMessage(
                      messages,
                      'cartScreen',
                      'aria.qtyListbox',
                      'Quantity options for {{name}}',
                    ).replace('{{name}}', item.name || '')}
                    popperConfig={{
                      placement: 'bottom',
                      modifiers: [
                        { name: 'offset', options: { offset: [0, 6] } },
                        {
                          name: 'flip',
                          options: { fallbackPlacements: [] },
                        },
                      ],
                    }}
                  >
                    {Array.from({ length: maxQty }, (_, index) => index).map(
                      (index) => (
                        <Dropdown.Item
                          key={index + 1}
                          id={`cart-item-qty-option-${lineId}-${index + 1}`}
                          active={
                            clampedQty === index + 1 || focusedIdx === index
                          }
                          onMouseEnter={() => setFocusedIdx(index)}
                          onClick={() => {
                            onQtyChange(lineId, index + 1);
                            setOpenQty(false);
                          }}
                          role="option"
                          aria-selected={clampedQty === index + 1}
                        >
                          {index + 1}
                        </Dropdown.Item>
                      ),
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </label>
            </Col>
            <Col xs="auto" className="text-end">
              <Button
                variant="link"
                className="remove-from-cart-btn custom-btn-link flush"
                onClick={() => onRemove(item)}
                aria-label={readCartMessage(
                  messages,
                  'cartScreen',
                  'aria.removeItem',
                  'Remove {{name}} from cart',
                ).replace('{{name}}', item.name || '')}
              >
                <Image
                  src="/icons/bin.png"
                  width={30}
                  height={30}
                  alt={readCartMessage(
                    messages,
                    'cartScreen',
                    'remove',
                    'Remove',
                  )}
                />
              </Button>
            </Col>
          </>
        )}
      </NoGutterRow>
    </article>
  );
}
